// src/Pages/ModuleRecords/InfrastructureAddRecord.jsx
import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import RecordWizard from '../../component/reusable/formRecords/RecordWizards';
import { Field, SectionHead, SectionDivider, ReadonlyBox, Select, INP, INP_LOCKED } from '../../component/reusable/formRecords/formFieldPrimitives';
import { useLocationOptions } from '../../component/Admin/shared/useLocationOptions';
import GpsCoordinateField from '../../component/shared/GpsCoordinateField';
import RegistryCustomFields from '../../component/Admin/FoundationalTables/FormRegistry/RegistryCustomFields';
import { RegistryFormScope, useRegistryLabel } from '../../component/Admin/FoundationalTables/FormRegistry/registryFieldOverrides';
import {
  facilitiesApi,
  facilityLookupsApi,
  FACILITY_TYPE_OPTIONS,
  FUNCTIONAL_STATUS_OPTIONS,
  OWNERSHIP_OPTIONS,
  LICENSED_STATUS_OPTIONS,
} from '../../api/facilitiesApi';
import { feedProductionApi, normalizeFeedProductionRow } from '../../api/feedProductionApi';
import { humanizeWorkflowStatus } from '../../component/Infrastructure/shared/FacilityWorkflow';
import FacilityClimateSections, {
  EMPTY_CLIMATE_STATE, FacilityClimateReview, applyClimateMetadata, isClimateStateValid, readClimateState, useClimateLookupLabels,
} from '../../component/Infrastructure/shared/FacilityClimateSections';
import { BIOGAS_SYSTEM_TYPE } from '../../component/Infrastructure/shared/climateOptions';
import FacilityStatusUpdateRecord from './FacilityStatusUpdateRecord';
import { useAuth } from '../../context/AuthContext';

const MODULE_NAME = 'Infrastructure & Services';

const ALL_SUB_MODULES = [
  { value: 'core', label: 'Facility Core Registry' },
  { value: 'status', label: 'Facility Status Update' },
];

const FEED_TYPES   = ['Compound', 'Pellet', 'Mash', 'Supplement', 'Premix', 'Roughage', 'Total Mixed Ration', 'Other'];
const UNITS        = ['Kg', 'Tonnes', 'Bags 50kg', 'Bags 25kg'];
const GRADES       = ['Grade A', 'Grade B', 'Grade C'];
const SPECIES_OPTS = ['Cattle', 'Poultry', 'Pigs', 'Sheep & Goats', 'Fish', 'Cattle & Sheep'];

const STEPS_BY_SUBMODULE = {
  core: [
    { key: 'geospatial', label: 'Geospatial' },
    { key: 'identification', label: 'Facility Identification' },
    { key: 'review',         label: 'Review' },
  ],
  feed: [
    { key: 'geospatial', label: 'Geospatial' },
    { key: 'identification', label: 'Feed Production Details' },
    { key: 'review',         label: 'Review' },
  ],
};

const TAB_ROUTE = {
  core: '/infrastructure/core',
  feed: '/infrastructure/feed',
};

function parseError(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
}

function normalizeOption(option) {
  if (option === null || option === undefined) return null;
  if (typeof option === 'string' || typeof option === 'number') {
    const text = String(option).trim();
    return text ? { value: text, label: text } : null;
  }
  const value = String(option.value ?? option.id ?? option.code ?? option.label ?? option.name ?? '').trim();
  if (!value) return null;
  const label = String(option.label ?? option.name ?? option.title ?? value).trim() || value;
  return { value, label };
}

function labelFor(options, value) {
  return options.map(normalizeOption).filter(Boolean).find((o) => o.value === value)?.label ?? value;
}

function ReviewRow({ label: pageLabel, value }) {
  const labelText = useRegistryLabel(pageLabel);
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-xs font-semibold text-gray-500">{labelText}</span>
      <span className="text-sm text-gray-900 text-right">{value || value === 0 ? String(value) : '—'}</span>
    </div>
  );
}

// Facility records reference an approved registry entry — registry-first.
// Picking an existing facility here must link to that facility's real row
// (by uuid), never re-create it under the same name. New entries are
// handled through the cross-cutting Registry Proposal workflow.
function useExistingFacilities(subModule) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    let active = true;
    const api = subModule === 'feed' ? feedProductionApi : facilitiesApi;
    api.listAll({ per_page: 500 })
      .then(({ items }) => {
        if (!active) return;
        const seen = new Set();
        const unique = [];
        (items ?? []).forEach((i) => {
          const uuid = i.uuid ?? i.id ?? null;
          const name = String(i.name ?? i.facilityName ?? '').trim();
          if (!uuid || !name || seen.has(uuid)) return;
          seen.add(uuid);
          unique.push({ value: uuid, label: name });
        });
        unique.sort((a, b) => a.label.localeCompare(b.label));
        setOptions(unique);
      })
      .catch(() => { if (active) setOptions([]); });
    return () => { active = false; };
  }, [subModule]);
  return options;
}

// DCIC-06 facility_id — registry-first: pick the facility from the registry
// (opens its record for update), or choose "+ Propose New Facility" to unlock
// the Name field. A name is only typed when proposing a new facility.
const PROPOSE_NEW = '__propose_new__';
function FacilityNameField({ onSelectExisting, nameValue, onNameChange, existingOptions }) {
  const [proposing, setProposing] = useState(Boolean(nameValue));
  const options = [...existingOptions, { value: PROPOSE_NEW, label: '+ Propose New Facility' }];
  const pick = (e) => {
    const value = e.target.value;
    if (value === PROPOSE_NEW) { setProposing(true); return; }
    setProposing(false);
    onNameChange('');
    if (value) onSelectExisting(value);
  };
  return (
    <>
      <Field label="Facility" required hint="Select from the registry to update it, or Propose New if it is not registered yet.">
        <Select value={proposing ? PROPOSE_NEW : ''} onChange={pick} options={options} placeholder="Select from registry" />
      </Field>
      <Field label="Facility Name" required={proposing} hint="On Propose New only.">
        <input
          value={nameValue}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={!proposing}
          placeholder={proposing ? 'Enter facility name' : '(On Propose New only)'}
          className={proposing ? INP : INP_LOCKED}
        />
      </Field>
    </>
  );
}

const EMPTY_CORE = {
  name: '', facility_type: '', functional_status: '', ownership: '', licensed_status: '', upgrade_priority: '',
  state: '', lga: '', ward: '', gps_coordinates: '', assessment_notes: '',
  daily_slaughter_capacity: '', annual_production_capacity: '', cold_storage_capacity: '',
  cold_storage_type: '', daily_milk_capacity_litres: '', water_source: '',
};

const EMPTY_FEED = {
  facilityName: '', feedType: '', volume: '', unit: '', capacityUtil: '', targetSpecies: '', qualityGrade: '', pricePerUnit: '',
  state: '', lga: '', ward: '', gps_coordinates: '',
};

// Capacity keys live at the top level of capacity_metadata; they are rebuilt
// from the form on every save, while anything else already stored there
// (e.g. the MRV+C status) is carried over.
const CAPACITY_KEYS = [
  'daily_slaughter_capacity', 'annual_production_capacity', 'cold_storage_capacity',
  'cold_storage_type', 'daily_milk_capacity_litres', 'water_source',
];

const CAPACITY_TYPE_GROUPS = {
  abattoir:  ['abattoir', 'slaughter_slab', 'slaughter_house'],
  feedMill:  ['feed_mill', 'feedmill'],
  coldStore: ['cold_storage_commercial', 'cold_storage_biological'],
};

// DCIC-07 Facility Status Update has its own record page; the Sub Module
// selector on either page switches between them via ?subModule=.
export default function InfrastructureAddRecord({ viewOnly = false }) {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  if (searchParams.get('subModule') === 'status') return <FacilityStatusUpdateRecord viewOnly={viewOnly} />;
  // R05 has no access to Facility Core Registry records.
  if (user?.roleId === 'R05') return <Navigate to="/infrastructure/status" replace />;
  return <FacilityRecordWizard viewOnly={viewOnly} />;
}

function FacilityRecordWizard({ viewOnly = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams();
  const isEditMode = Boolean(uuid);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const roleId = user?.roleId ?? 'R06';
  const isR04 = roleId === 'R04';
  const isR05 = roleId === 'R05';
  const isStateLocked = isR04 || isR05;
  const isLgaLocked = isR05;

  const initialSubModule = ALL_SUB_MODULES.some((s) => s.value === searchParams.get('subModule'))
    ? searchParams.get('subModule')
    : 'core';
  const [subModule, setSubModuleState] = useState(initialSubModule);
  const setSubModule = (value) => {
    setSubModuleState(value);
    setStep(0);
    setError('');
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('subModule', value); return next; }, { replace: true });
  };

  const [step, setStep] = useState(0);
  const steps = STEPS_BY_SUBMODULE[subModule] ?? STEPS_BY_SUBMODULE.core;
  const scopeStateDefault = isStateLocked ? (user?.assignedState ?? '') : '';
  const scopeLgaDefault = isLgaLocked ? (user?.assignedLga ?? '') : '';
  const scopeWardDefault = isLgaLocked ? (user?.assignedWard ?? '') : '';

  const [coreForm, setCoreForm] = useState(EMPTY_CORE);
  const [climateState, setClimateState] = useState(EMPTY_CLIMATE_STATE);
  const climateLookupLabel = useClimateLookupLabels(coreForm.facility_type);
  const [feedForm, setFeedForm] = useState(EMPTY_FEED);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [editRow, setEditRow] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(isEditMode);

  const existingFacilityOptions = useExistingFacilities(subModule);
  const goToExistingFacility = (facilityUuid) => {
    navigate(`/infrastructure/edit/${facilityUuid}?subModule=${subModule}`);
  };

  const [facilityTypeOptions, setFacilityTypeOptions] = useState(FACILITY_TYPE_OPTIONS);
  useEffect(() => {
    let active = true;
    facilityLookupsApi.facilityTypes().then((opts) => { if (active) setFacilityTypeOptions(opts); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (isEditMode) return;
    setCoreForm({
      ...EMPTY_CORE,
      state: scopeStateDefault,
      lga: scopeLgaDefault,
      ward: scopeWardDefault,
    });
    setFeedForm({
      ...EMPTY_FEED,
      state: scopeStateDefault,
      lga: scopeLgaDefault,
      ward: scopeWardDefault,
    });
  }, [isEditMode, scopeStateDefault, scopeLgaDefault, scopeWardDefault]);

  const activeState = subModule === 'feed' ? feedForm.state : coreForm.state;
  const activeLga    = subModule === 'feed' ? feedForm.lga   : coreForm.lga;
  const { stateOptions, lgaOptions, wardOptions, loadingLgas, loadingWards } = useLocationOptions(activeState, activeLga);

  // ── Edit-mode: seed from the row handed over via navigation state, falling
  // back to a live GET — same pattern as MarketAddRecord/LivestockAddRecord ──
  useEffect(() => {
    if (!isEditMode) return;
    setLoadingRecord(true); setError('');
    const seedRow = location.state?.row;
    if (subModule === 'feed') {
      if (seedRow && seedRow.id === uuid) { setEditRow(seedRow); setLoadingRecord(false); return; }
      facilitiesApi.get(uuid)
        .then((raw) => setEditRow(normalizeFeedProductionRow({ raw })))
        .catch((e) => setError(parseError(e, 'Failed to load record.')))
        .finally(() => setLoadingRecord(false));
    } else {
      if (seedRow && (seedRow.uuid === uuid || seedRow.id === uuid)) { setEditRow(seedRow); setLoadingRecord(false); return; }
      facilitiesApi.get(uuid)
        .then((row) => setEditRow(row))
        .catch((e) => setError(parseError(e, 'Failed to load record.')))
        .finally(() => setLoadingRecord(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, uuid, subModule]);

  const currentStatus = String(editRow?.data_status ?? editRow?.dataStatus ?? 'draft').toLowerCase();
  // R04 and R05 may edit their facility records while Draft / Rejected.
  const canEditContent = !isEditMode || roleId === 'R01' || (['R04', 'R05'].includes(roleId) && ['draft', 'rejected'].includes(currentStatus));
  const fieldsLocked = isEditMode && !canEditContent;
  const editModeLabel = isEditMode
    ? (subModule === 'feed' ? 'Edit Feed Production Record' : 'Edit Record')
    : '';
  const editModeSubtitle = isEditMode
    ? (subModule === 'feed'
        ? 'Update the details for this feed production record.'
        : 'Update the details for this record.')
    : '';

  // ── Core: pre-fill on load ───────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !editRow || subModule !== 'core') return;
    const cap = editRow.capacity_metadata ?? {};
    setCoreForm({
      name:                        editRow.name              ?? '',
      facility_type:               editRow.facility_type     ?? '',
      functional_status:           editRow.functional_status ?? '',
      ownership:                   editRow.ownership_raw     ?? '',
      licensed_status:             editRow.licensed_status   ?? '',
      upgrade_priority:            editRow.upgrade_priority  ?? '',
      state:                       editRow.state             ?? '',
      lga:                         editRow.lga               ?? '',
      ward:                        editRow.ward              ?? '',
      gps_coordinates:             editRow.gps_coordinates   ?? '',
      assessment_notes:            editRow.assessment_notes  ?? '',
      daily_slaughter_capacity:    String(cap.daily_slaughter_capacity   ?? ''),
      annual_production_capacity:  String(cap.annual_production_capacity ?? ''),
      cold_storage_capacity:       String(cap.cold_storage_capacity      ?? ''),
      cold_storage_type:           cap.cold_storage_type           ?? '',
      daily_milk_capacity_litres:  String(cap.daily_milk_capacity_litres ?? ''),
      water_source:                cap.water_source                ?? '',
    });
    setClimateState(readClimateState(editRow));
  }, [isEditMode, editRow, subModule]);

  // ── Feed: pre-fill on load ───────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !editRow || subModule !== 'feed') return;
    setFeedForm({
      facilityName:  editRow.facilityName  ?? '',
      feedType:      editRow.feedType      ?? '',
      volume:        editRow.volume        ?? '',
      unit:          editRow.unit          ?? '',
      capacityUtil:  editRow.capacityUtil  ?? '',
      targetSpecies: editRow.targetSpecies ?? '',
      qualityGrade:  editRow.qualityGrade  ?? '',
      pricePerUnit:  editRow.pricePerUnit  ?? '',
      state:         editRow.state         ?? '',
      lga:           editRow.lga           ?? '',
      ward:          editRow.ward          ?? '',
      gps_coordinates: editRow.gps_coordinates ?? '',
    });
  }, [isEditMode, editRow, subModule]);

  const setCore = (key) => (e) => setCoreForm((p) => {
    const v = e && e.target ? e.target.value : e;
    if (key === 'state') {
      if (isStateLocked) return p;
      return { ...p, state: v, lga: '', ward: '' };
    }
    if (key === 'lga') {
      if (isLgaLocked) return p;
      return { ...p, lga: v, ward: '' };
    }
    return { ...p, [key]: v };
  });
  const setFeed = (key) => (e) => setFeedForm((p) => {
    const v = e && e.target ? e.target.value : e;
    if (key === 'state') {
      if (isStateLocked) return p;
      return { ...p, state: v, lga: '', ward: '' };
    }
    if (key === 'lga') {
      if (isLgaLocked) return p;
      return { ...p, lga: v, ward: '' };
    }
    return { ...p, [key]: v };
  });

  const goToTab = () => navigate(TAB_ROUTE[subModule]);
  const hasLocationOption = (options, value) => options.some((option) => option.value === value);
  const validateGeoSelection = (form) => {
    if (!form.state || !form.lga) {
      setError('State and LGA are required.');
      return false;
    }
    if (!loadingLgas && (!lgaOptions.length || !hasLocationOption(lgaOptions, form.lga))) {
      setError(`No geospatial reference row for state=${form.state}, lga=${form.lga}.`);
      return false;
    }
    if (form.ward && !loadingWards && (!wardOptions.length || !hasLocationOption(wardOptions, form.ward))) {
      setError(`No geospatial reference row for state=${form.state}, lga=${form.lga}.`);
      return false;
    }
    return true;
  };

  // ── Step validity ────────────────────────────────────────────────────────
  const coreGeoValid = Boolean(coreForm.state && coreForm.lga && coreForm.gps_coordinates);
  const coreFt = (coreForm.facility_type ?? '').toLowerCase();
  const coreIsAbattoir  = CAPACITY_TYPE_GROUPS.abattoir.includes(coreFt);
  const coreIsFeedMill  = CAPACITY_TYPE_GROUPS.feedMill.includes(coreFt);
  const coreIsColdStore = CAPACITY_TYPE_GROUPS.coldStore.includes(coreFt);
  const coreIsMilk      = coreFt === 'milk_collection_centre';
  const coreIsWatering  = coreFt === 'watering_point';
  const coreCapacityValid =
    (!coreIsAbattoir  || Boolean(coreForm.daily_slaughter_capacity)) &&
    (!coreIsFeedMill  || Boolean(coreForm.annual_production_capacity)) &&
    (!coreIsColdStore || Boolean(coreForm.cold_storage_capacity && coreForm.cold_storage_type)) &&
    (!coreIsMilk      || Boolean(coreForm.daily_milk_capacity_litres)) &&
    (!coreIsWatering  || Boolean(coreForm.water_source));
  const coreStepValid = Boolean(coreForm.name.trim() && coreForm.facility_type && coreForm.functional_status && coreForm.ownership && coreForm.licensed_status)
    && coreCapacityValid && isClimateStateValid(coreForm.facility_type, climateState);
  const feedGeoValid = Boolean(feedForm.state && feedForm.lga);
  const feedStepValid = Boolean(feedForm.facilityName.trim() && feedForm.feedType && feedForm.volume.trim() && feedForm.unit);
  const stepValidBySubmodule = {
    core: [coreGeoValid, coreStepValid, true],
    feed: [feedGeoValid, feedStepValid, true],
  };
  const stepValid = fieldsLocked ? true : (stepValidBySubmodule[subModule]?.[step] ?? false);

  // ── Core: Create or Update ───────────────────────────────────────────────
  const buildCorePayload = () => {
    const existing = editRow?.capacity_metadata && typeof editRow.capacity_metadata === 'object' ? editRow.capacity_metadata : {};
    const rebuiltKeys = [...CAPACITY_KEYS, 'ward', 'gps_coordinates', 'assessment_notes'];
    const cap = Object.fromEntries(Object.entries(existing).filter(([key]) => !rebuiltKeys.includes(key)));
    if (coreForm.daily_slaughter_capacity)   cap.daily_slaughter_capacity   = Number(coreForm.daily_slaughter_capacity);
    if (coreForm.annual_production_capacity) cap.annual_production_capacity  = Number(coreForm.annual_production_capacity);
    if (coreForm.cold_storage_capacity)      cap.cold_storage_capacity       = Number(coreForm.cold_storage_capacity);
    if (coreForm.cold_storage_type)          cap.cold_storage_type           = coreForm.cold_storage_type;
    if (coreForm.daily_milk_capacity_litres) cap.daily_milk_capacity_litres  = Number(coreForm.daily_milk_capacity_litres);
    if (coreForm.water_source)               cap.water_source                = coreForm.water_source;
    return {
      name:              coreForm.name.trim(),
      facility_type:     coreForm.facility_type,
      functional_status: coreForm.functional_status,
      ownership:         coreForm.ownership,
      licensed_status:   coreForm.licensed_status   || null,
      upgrade_priority:  coreForm.upgrade_priority  || null,
      state:             coreForm.state,
      lga:               coreForm.lga               || null,
      ward:              coreForm.ward               || null,
      gps_coordinates:   coreForm.gps_coordinates   || null,
      assessment_notes:  coreForm.assessment_notes.trim() || null,
      ...(isEditMode ? {} : { data_status: 'draft' }),
      capacity_metadata: applyClimateMetadata(coreForm.facility_type, climateState, cap),
    };
  };
  const handleCoreSave = async () => {
    if (saving || fieldsLocked) return;
    if (!validateGeoSelection(coreForm)) return;
    setSaving(true); setError('');
    try {
      if (isEditMode) await facilitiesApi.update(uuid, buildCorePayload());
      else await facilitiesApi.create(buildCorePayload());
      navigate(TAB_ROUTE.core);
    } catch (e) {
      setError(parseError(e, isEditMode ? 'Failed to update facility record.' : 'Failed to create facility record.'));
      setSaving(false);
    }
  };

  // ── Feed: Create or Update (rides on facilitiesApi via feedProductionApi) ──
  const handleFeedSave = async () => {
    if (saving || fieldsLocked) return;
    if (!validateGeoSelection(feedForm)) return;
    setSaving(true); setError('');
    const rowLike = {
      facilityName:  feedForm.facilityName.trim(),
      feedType:      feedForm.feedType,
      volume:        feedForm.volume.trim(),
      unit:          feedForm.unit,
      capacityUtil:  feedForm.capacityUtil.trim(),
      targetSpecies: feedForm.targetSpecies,
      qualityGrade:  feedForm.qualityGrade,
      pricePerUnit:  feedForm.pricePerUnit.trim(),
      state:         feedForm.state,
      lga:           feedForm.lga,
      ward:          feedForm.ward,
      gps_coordinates: feedForm.gps_coordinates,
    };
    try {
      if (isEditMode) await feedProductionApi.update(uuid, rowLike, user, { raw: editRow?.raw ?? editRow });
      else await feedProductionApi.create(rowLike, user);
      navigate(TAB_ROUTE.feed);
    } catch (e) {
      setError(parseError(e, isEditMode ? 'Failed to update feed production record.' : 'Failed to create feed production record.'));
      setSaving(false);
    }
  };

  // ── "Select Module" block — shared header for every sub-module's first step ──
  function renderSelectModule() {
    return (
      <>
        <SectionHead title="Select Module" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Module" required>
            <Select value={MODULE_NAME} disabled className={INP_LOCKED} options={[{ value: MODULE_NAME, label: MODULE_NAME }]} />
          </Field>
          <Field label="Sub Module" required>
            <Select value={subModule} onChange={(e) => setSubModule(e.target.value)} options={ALL_SUB_MODULES} disabled={isEditMode}
              className={isEditMode ? INP_LOCKED : INP} />
          </Field>
        </div>
        <SectionDivider />
      </>
    );
  }

  // ── Core step content ────────────────────────────────────────────────────
  function renderCoreIdentification() {
    const ft = (coreForm.facility_type ?? '').toLowerCase();
    const isAbattoir  = CAPACITY_TYPE_GROUPS.abattoir.includes(ft);
    const isFeedMill  = CAPACITY_TYPE_GROUPS.feedMill.includes(ft);
    const isColdStore = CAPACITY_TYPE_GROUPS.coldStore.includes(ft);
    const isMilk      = ft === 'milk_collection_centre';
    const isWatering  = ft === 'watering_point';
    const hasCapacity = isAbattoir || isFeedMill || isColdStore || isMilk || isWatering;
    const fieldCls = fieldsLocked ? INP_LOCKED : INP;

    if (step === 0) {
      return (
        <div className="space-y-5">
          <div className="space-y-4">
            {renderSelectModule()}
            <SectionHead title="Geospatial" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="State" required>
                {isStateLocked ? (
                  <input value={coreForm.state} readOnly className={INP_LOCKED} />
                ) : (
                  <Select value={coreForm.state} onChange={setCore('state')} options={stateOptions} disabled={fieldsLocked} placeholder="Select state" className={fieldCls} />
                )}
              </Field>
              <Field label="LGA" required>
                {isLgaLocked ? (
                  <input value={coreForm.lga} readOnly className={INP_LOCKED} />
                ) : (
                  <Select value={coreForm.lga} onChange={setCore('lga')} options={lgaOptions} disabled={fieldsLocked || !coreForm.state || loadingLgas}
                    placeholder={coreForm.state ? (loadingLgas ? 'Loading LGAs…' : 'Select LGA') : 'Select state first'} className={fieldCls} />
                )}
              </Field>
              <Field label="Ward">
                <Select value={coreForm.ward} onChange={setCore('ward')} options={wardOptions} disabled={fieldsLocked || !coreForm.lga || loadingWards}
                  placeholder={coreForm.lga ? (loadingWards ? 'Loading wards…' : 'Select Ward') : 'Select LGA first'} className={fieldCls} />
              </Field>
            </div>
            <GpsCoordinateField label="GPS Coordinates" required value={coreForm.gps_coordinates} onChange={setCore('gps_coordinates')} disabled={fieldsLocked} />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <div className="space-y-4">
          <SectionHead title="Facility Identification" />
          {isEditMode ? (
            <Field label="Facility Name" required>
              <input value={coreForm.name} onChange={setCore('name')} disabled={fieldsLocked} placeholder="Enter facility name" className={fieldCls} />
            </Field>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <FacilityNameField
                onSelectExisting={goToExistingFacility}
                nameValue={coreForm.name}
                onNameChange={(v) => setCoreForm((p) => ({ ...p, name: v }))}
                existingOptions={existingFacilityOptions}
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Facility Type" required>
              <Select value={coreForm.facility_type} onChange={setCore('facility_type')} options={facilityTypeOptions} disabled={fieldsLocked} className={fieldCls} />
            </Field>
            <Field label="Functional Status" required hint={ft === BIOGAS_SYSTEM_TYPE ? 'For a biogas system, Active means operational.' : undefined}>
              <Select value={coreForm.functional_status} onChange={setCore('functional_status')} options={FUNCTIONAL_STATUS_OPTIONS} disabled={fieldsLocked} className={fieldCls} />
            </Field>
            <Field label="Licensed Status" required>
              <Select value={coreForm.licensed_status} onChange={setCore('licensed_status')} options={LICENSED_STATUS_OPTIONS} disabled={fieldsLocked} className={fieldCls} />
            </Field>
            <Field label="Ownership" required>
              <Select value={coreForm.ownership} onChange={setCore('ownership')} options={OWNERSHIP_OPTIONS} disabled={fieldsLocked} className={fieldCls} />
            </Field>
          </div>

        </div>

        {hasCapacity && (
          <>
            <SectionDivider />
            <div className="space-y-4">
              <SectionHead title="Capacity Details" />
              <div className="grid grid-cols-2 gap-4">
                {isAbattoir && (
                  <div className="col-span-2">
                    <Field label="Daily Slaughter Capacity (Head/day)" required>
                      <input type="number" value={coreForm.daily_slaughter_capacity} onChange={setCore('daily_slaughter_capacity')} disabled={fieldsLocked} placeholder="e.g. 200" className={fieldCls} />
                    </Field>
                  </div>
                )}
                {isFeedMill && (
                  <div className="col-span-2">
                    <Field label="Annual Production Capacity (Tonnes/year)" required>
                      <input type="number" value={coreForm.annual_production_capacity} onChange={setCore('annual_production_capacity')} disabled={fieldsLocked} placeholder="e.g. 5000" className={fieldCls} />
                    </Field>
                  </div>
                )}
                {isColdStore && (<>
                  <Field label="Cold Storage Capacity (Tonnes)" required>
                    <input type="number" value={coreForm.cold_storage_capacity} onChange={setCore('cold_storage_capacity')} disabled={fieldsLocked} placeholder="e.g. 200" className={fieldCls} />
                  </Field>
                  <Field label="Cold Storage Type" required>
                    <input value={coreForm.cold_storage_type} onChange={setCore('cold_storage_type')} disabled={fieldsLocked} placeholder="e.g. Refrigerated" className={fieldCls} />
                  </Field>
                </>)}
                {isMilk && (
                  <div className="col-span-2">
                    <Field label="Daily Milk Capacity (Litres/day)" required>
                      <input type="number" value={coreForm.daily_milk_capacity_litres} onChange={setCore('daily_milk_capacity_litres')} disabled={fieldsLocked} placeholder="e.g. 500" className={fieldCls} />
                    </Field>
                  </div>
                )}
                {isWatering && (
                  <div className="col-span-2">
                    <Field label="Water Source" required>
                      <input value={coreForm.water_source} onChange={setCore('water_source')} disabled={fieldsLocked} placeholder="e.g. River, Borehole, Dam" className={fieldCls} />
                    </Field>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <FacilityClimateSections
          facilityType={coreForm.facility_type}
          facilityUuid={isEditMode ? uuid : null}
          state={climateState}
          onChange={setClimateState}
          locked={fieldsLocked}
          metadata={editRow?.capacity_metadata ?? {}}
        />

        <SectionDivider />

        <div className="space-y-4">
          <SectionHead title="Assessment" />
          <Field label="Assessment Notes" hint={`${coreForm.assessment_notes.length}/500`}>
            <textarea value={coreForm.assessment_notes} onChange={setCore('assessment_notes')} disabled={fieldsLocked} rows={3} maxLength={500} placeholder="Observations from the assessment" className={`${fieldCls} resize-none`} />
          </Field>
        </div>

        <SectionDivider />

        <div className="space-y-4">
          <SectionHead title="System" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Record Status">
              <ReadonlyBox>{isEditMode ? `${currentStatus.charAt(0).toUpperCase()}${currentStatus.slice(1)} (current)` : 'Draft (system-set)'}</ReadonlyBox>
            </Field>
          </div>
        </div>
      </div>
    );
  }

  function renderCoreReview() {
    const cap = coreForm;
    return (
      <>
        <SectionHead title="Facility Identification" />
        <ReviewRow label="Facility Name" value={coreForm.name} />
        <ReviewRow label="Facility Type" value={labelFor(facilityTypeOptions, coreForm.facility_type)} />
        <ReviewRow label="Functional Status" value={labelFor(FUNCTIONAL_STATUS_OPTIONS, coreForm.functional_status)} />
        <ReviewRow label="Licensed Status" value={labelFor(LICENSED_STATUS_OPTIONS, coreForm.licensed_status)} />
        <ReviewRow label="Ownership" value={labelFor(OWNERSHIP_OPTIONS, coreForm.ownership)} />
        <ReviewRow label="Assessment Notes" value={coreForm.assessment_notes} />
        <SectionHead title="Geospatial" />
        <ReviewRow label="State" value={coreForm.state} />
        <ReviewRow label="LGA" value={coreForm.lga} />
        <ReviewRow label="Ward" value={coreForm.ward} />
        {cap.daily_slaughter_capacity && <ReviewRow label="Daily Slaughter Capacity" value={`${cap.daily_slaughter_capacity} head/day`} />}
        {cap.annual_production_capacity && <ReviewRow label="Annual Production Capacity" value={`${cap.annual_production_capacity} tonnes/yr`} />}
        {cap.cold_storage_capacity && <ReviewRow label="Cold Storage Capacity" value={`${cap.cold_storage_capacity} tonnes`} />}
        {cap.cold_storage_type && <ReviewRow label="Cold Storage Type" value={cap.cold_storage_type} />}
        {cap.daily_milk_capacity_litres && <ReviewRow label="Daily Milk Capacity" value={`${cap.daily_milk_capacity_litres} L/day`} />}
        {cap.water_source && <ReviewRow label="Water Source" value={cap.water_source} />}
        <FacilityClimateReview
          facilityType={coreForm.facility_type}
          facilityUuid={isEditMode ? uuid : null}
          state={climateState}
          metadata={editRow?.capacity_metadata ?? {}}
          ReviewRow={ReviewRow}
          lookupLabel={climateLookupLabel}
        />
        <SectionHead title="System" />
        <ReviewRow label="Record Status" value={isEditMode ? humanizeWorkflowStatus(currentStatus) : 'Draft (system-set)'} />
      </>
    );
  }

  // ── Feed step content ────────────────────────────────────────────────────
  function renderFeedIdentification() {
    const fieldCls = fieldsLocked ? INP_LOCKED : INP;

    if (step === 0) {
      return (
        <div className="space-y-5">
          <div className="space-y-4">
            {renderSelectModule()}
            <SectionHead title="Geospatial" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="State" required>
                {isStateLocked ? (
                  <input value={feedForm.state} readOnly className={INP_LOCKED} />
                ) : (
                  <Select value={feedForm.state} onChange={setFeed('state')} options={stateOptions} disabled={fieldsLocked} placeholder="Select state" className={fieldCls} />
                )}
              </Field>
              <Field label="LGA" required>
                {isLgaLocked ? (
                  <input value={feedForm.lga} readOnly className={INP_LOCKED} />
                ) : (
                  <Select value={feedForm.lga} onChange={setFeed('lga')} options={lgaOptions} disabled={fieldsLocked || !feedForm.state || loadingLgas}
                    placeholder={feedForm.state ? (loadingLgas ? 'Loading LGAs…' : 'Select LGA') : 'Select state first'} className={fieldCls} />
                )}
              </Field>
              <Field label="Ward">
                <Select value={feedForm.ward} onChange={setFeed('ward')} options={wardOptions} disabled={fieldsLocked || !feedForm.lga || loadingWards}
                  placeholder={feedForm.lga ? (loadingWards ? 'Loading wards…' : 'Select Ward') : 'Select LGA first'} className={fieldCls} />
              </Field>
            </div>
            <GpsCoordinateField label="GPS Coordinates" value={feedForm.gps_coordinates} onChange={setFeed('gps_coordinates')} disabled={fieldsLocked} />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <div className="space-y-4">
          <SectionHead title="Feed Production Details" />
          {isEditMode ? (
            <Field label="Facility Name" required>
              <input value={feedForm.facilityName} onChange={setFeed('facilityName')} disabled={fieldsLocked} placeholder="Enter facility name" className={fieldCls} />
            </Field>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <FacilityNameField
                onSelectExisting={goToExistingFacility}
                nameValue={feedForm.facilityName}
                onNameChange={(v) => setFeedForm((p) => ({ ...p, facilityName: v }))}
                existingOptions={existingFacilityOptions}
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Feed Type" required>
              <Select value={feedForm.feedType} onChange={setFeed('feedType')} options={FEED_TYPES} disabled={fieldsLocked} className={fieldCls} />
            </Field>
            <Field label="Volume Produced" required>
              <input value={feedForm.volume} onChange={setFeed('volume')} disabled={fieldsLocked} placeholder="e.g. 500" className={fieldCls} />
            </Field>
            <Field label="Unit" required>
              <Select value={feedForm.unit} onChange={setFeed('unit')} options={UNITS} disabled={fieldsLocked} className={fieldCls} />
            </Field>
            <Field label="Capacity Utilisation (%)">
              <input value={feedForm.capacityUtil} onChange={setFeed('capacityUtil')} disabled={fieldsLocked} placeholder="e.g. 85%" className={fieldCls} />
            </Field>
            <Field label="Target Species">
              <Select value={feedForm.targetSpecies} onChange={setFeed('targetSpecies')} options={SPECIES_OPTS} disabled={fieldsLocked} className={fieldCls} />
            </Field>
            <Field label="Feed Quality Grade">
              <Select value={feedForm.qualityGrade} onChange={setFeed('qualityGrade')} options={GRADES} disabled={fieldsLocked} className={fieldCls} />
            </Field>
            <Field label="Price per Unit (₦/kg)">
              <input value={feedForm.pricePerUnit} onChange={setFeed('pricePerUnit')} disabled={fieldsLocked} placeholder="e.g. 85" className={fieldCls} />
            </Field>
          </div>
        </div>

        <SectionDivider />

        <div className="space-y-4">
          <SectionHead title="System" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Confidence Level"><ReadonlyBox>auto · system-set</ReadonlyBox></Field>
            <Field label="Record Status">
              <ReadonlyBox>{isEditMode ? `${currentStatus.charAt(0).toUpperCase()}${currentStatus.slice(1)} (current)` : 'Draft (system-set)'}</ReadonlyBox>
            </Field>
          </div>
        </div>
      </div>
    );
  }

  function renderFeedReview() {
    return (
      <>
        <SectionHead title="Feed Production Details" />
        <ReviewRow label="Facility Name" value={feedForm.facilityName} />
        <ReviewRow label="Feed Type" value={feedForm.feedType} />
        <ReviewRow label="Volume Produced" value={feedForm.volume} />
        <ReviewRow label="Unit" value={feedForm.unit} />
        <ReviewRow label="Capacity Utilisation (%)" value={feedForm.capacityUtil} />
        <ReviewRow label="Target Species" value={feedForm.targetSpecies} />
        <ReviewRow label="Feed Quality Grade" value={feedForm.qualityGrade} />
        <ReviewRow label="Price per Unit (₦/kg)" value={feedForm.pricePerUnit} />
        <SectionHead title="Geospatial" />
        <ReviewRow label="State" value={feedForm.state} />
        <ReviewRow label="LGA" value={feedForm.lga} />
        <ReviewRow label="Ward" value={feedForm.ward} />
        <SectionHead title="System" />
        <ReviewRow label="Confidence Level" value="auto · system-set" />
        <ReviewRow label="Record Status" value={isEditMode ? humanizeWorkflowStatus(currentStatus) : 'Draft (system-set)'} />
      </>
    );
  }

  const STEP_RENDERERS = {
    core: [renderCoreIdentification, renderCoreIdentification, renderCoreReview],
    feed: [renderFeedIdentification, renderFeedIdentification, renderFeedReview],
  };
  const REVIEW_RENDERERS = {
    core: renderCoreReview,
    feed: renderFeedReview,
  };

  function renderReviewFooter() {
    const handleSave = subModule === 'feed' ? handleFeedSave : handleCoreSave;
    return (
      <>
        <button type="button" onClick={() => setStep((s) => s - 1)} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">Previous</button>
        <button type="button" onClick={handleSave} disabled={saving || fieldsLocked} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? (isEditMode ? 'Saving…' : 'Creating…') : (isEditMode ? 'Save Changes' : 'Create')}
        </button>
      </>
    );
  }

  const isLastStep = step === steps.length - 1;

  // ── View-only mode: Review summary + an Edit Record button for viewers
  // who are actually allowed to edit this record. ─────────────────────────
  if (viewOnly) {
    return (
      <div className="p-6 bg-white min-h-screen">
        {loadingRecord && (
          <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
            Loading record…
          </div>
        )}
        {error && (
          <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-xs text-red-700">
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div>
              <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
              <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-gray-500">Status:</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize bg-gray-100 text-gray-600">
                {currentStatus}
              </span>
            </div>
          </div>

          <div className="space-y-4">{REVIEW_RENDERERS[subModule]()}</div>

          <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
            <button type="button" onClick={goToTab} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
              Back
            </button>
            {roleId === 'R01' || (['R04', 'R05'].includes(roleId) && ['draft', 'rejected'].includes(currentStatus)) ? (
              <button
                type="button"
                onClick={() => navigate(`/infrastructure/edit/${uuid}?subModule=${subModule}`, { state: { row: editRow } })}
                className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700"
              >
                Edit Record
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      {loadingRecord && (
        <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
          Loading record…
        </div>
      )}

      {fieldsLocked && (
        <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          This record is <strong>{currentStatus}</strong> — field values are locked for your role.
        </div>
      )}

      <RegistryFormScope moduleKey="infrastructure" submoduleKey={subModule}>
        <RecordWizard
          steps={steps}
          currentStep={step}
          title={isEditMode ? editModeLabel : (subModule === 'feed' ? 'Add Feed Production Record' : 'Add New Record')}
          subtitle={isEditMode ? editModeSubtitle : (subModule === 'feed' ? 'Fill in the details to create a new feed production record' : 'Fill in the details to create a new record')}
          statusLabel={isEditMode ? currentStatus : 'draft'}
          error={error}
          onCancel={goToTab}
          onBack={() => setStep((s) => s - 1)}
          onNext={() => setStep((s) => s + 1)}
          canGoNext={stepValid}
          nextLabel="Next"
          footer={isLastStep ? <>{renderReviewFooter()}</> : undefined}
        >
          <RegistryCustomFields moduleKey="infrastructure" submoduleKey={subModule} />
          {STEP_RENDERERS[subModule][step]()}
        </RecordWizard>
      </RegistryFormScope>
    </div>
  );
}
